import { Box, CircularProgress, Typography, styled } from '@mui/material'
import React, { ReactNode, useCallback, useEffect, useLayoutEffect } from 'react'
import { colors } from '../constants/colors'
import { pageState } from '../context/page-context'
import { createEmptyArray } from '../utilities/array'

import { sitePaddingHorz } from '../constants'
import { isInBrowser } from '../utilities/browser'
import PaginationDots from './pagination-dots'

import { VBox } from '../elements/basic-elements'
import { excludeScProps } from '../utilities/browser'
import { MAX_RESULTS_WIDTH } from '../constants/sizes'

const THREE_CARD_BREAKPOINT = 1000
const TWO_CARD_BREAKPOINT = 650

let lastScroll = 0
let blockWheel = false
const pageOffset = 100
const ResultsList = ({
  results,
  total,
  title,
  loading,
  nextPage,
  fetchNextPage,
  onPageChange,
  children
}: {
  results?: {}[]
  total?: number
  title?: string
  loading: boolean
  nextPage?: number
  fetchNextPage?: Function
  onPageChange?: Function
  children?: (data: any) => ReactNode
}) => {
  const cardsRef = React.useRef<HTMLElement>(null)
  const { pageContentRef } = pageState()
  const [captureMovement, setCaptureMovement] = React.useState(false)
  const [activePage, setActivePage] = React.useState(0)
  const [tempOffset, setTempOffset] = React.useState(0)
  const [cardsPerPage, setCardsPerPage] = React.useState(1)
  const [cardsCanAnimate, setCardsCanAnimate] = React.useState(false)

  const totalResults = total || 0

  useEffect(() => {
    if (results && !cardsCanAnimate) {
      setCardsCanAnimate(true)
    }
  }, [results, cardsCanAnimate])

  const setPageSize = () => {
    if (isInBrowser()) {
      const { innerWidth } = window
      const newCardsPerPage =
        innerWidth >= THREE_CARD_BREAKPOINT ? 3 : innerWidth >= TWO_CARD_BREAKPOINT ? 2 : 1
      setCardsPerPage(newCardsPerPage)
    }
  }
  useLayoutEffect(() => {
    setPageSize()
    if (isInBrowser()) {
      window.addEventListener('resize', setPageSize)
      return () => window.removeEventListener('resize', setPageSize)
    }
  }, [])

  const numPages = Math.ceil(totalResults / cardsPerPage)
  const oneCard = numPages === 1

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // e.preventDefault()
    lastScroll = e.touches[0].clientX
    setCaptureMovement(true)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    // e.preventDefault()
    trackMovement(e.touches[0].clientX)
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // e.preventDefault()
    lastScroll = e.clientX
    setCaptureMovement(true)
  }
  const handleMoveEnd = () => {
    setCaptureMovement(false)
    setTempOffset(0)
  }
  const handlePageLeft = useCallback(() => {
    const lastPage = activePage >= numPages - 1
    const nextShown = (activePage + 2) * cardsPerPage
    const hasShownAllResults = nextShown > results.length
    const isOverScrolling = nextShown > results.length + 2
    const shouldLoadMore = hasShownAllResults && nextPage && !loading
    if (isOverScrolling) return
    if (shouldLoadMore || !lastPage) {
      setActivePage(activePage + 1)
      if (shouldLoadMore && fetchNextPage) {
        fetchNextPage()
      }
    }
  }, [activePage, numPages, cardsPerPage, loading])

  const handlePageRight = () => {
    if (activePage !== 0) {
      setActivePage(activePage - 1)
    }
  }
  const trackMovement = useCallback(
    (amount: number) => {
      const diff = amount - lastScroll
      if (diff < -25) {
        handleMoveEnd()
        handlePageLeft()
      } else if (diff > 25) {
        handleMoveEnd()
        handlePageRight()
      } else {
        setTempOffset(diff)
      }
    },
    [activePage, numPages, cardsPerPage, loading]
  )

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      trackMovement(e.clientX)
    },
    [activePage, numPages, cardsPerPage, loading]
  )

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (blockWheel) {
        if (Math.abs(e.deltaX) < 5) {
          blockWheel = false
        } else {
          return
        }
      }
      if (e.deltaX < -25) {
        blockWheel = true
        handlePageRight()
      } else if (e.deltaX > 25) {
        blockWheel = true
        handlePageLeft()
      }
    },
    [activePage, numPages, cardsPerPage, loading]
  )

  React.useEffect(() => {
    setTempOffset(0)
    const cards = Array.from(cardsRef.current?.children || []) as HTMLElement[]
    cards[activePage * cardsPerPage]?.focus()

    requestAnimationFrame(() => pageContentRef?.current?.scroll(0, 0))
  }, [cardsRef, activePage, cardsPerPage, pageContentRef])

  React.useEffect(() => {
    // this is to fix a bug where users scrolling very fast could
    // get the parent container to scroll its contents
    if (onPageChange) {
      onPageChange()
    }
  }, [activePage])

  React.useEffect(() => {
    setActivePage(0)
  }, [cardsPerPage])

  const getIndeces = (startingIndex: number, arrayLength: number) => {
    return arrayLength > 0 ? createEmptyArray(arrayLength).map((_i, i) => startingIndex + i) : []
  }

  const numResults = results?.length ?? 0
  const numActiveCards = Math.min(numResults - activePage * cardsPerPage, cardsPerPage)
  const activeIndeces = getIndeces(activePage * cardsPerPage, numActiveCards)

  const numCardsToTheLeft = Math.min(activeIndeces[0] || numResults - 1, cardsPerPage + 1)
  const numCardsToTheRight = Math.min(
    numResults - activeIndeces[activeIndeces.length - 1],
    cardsPerPage + 1
  )
  const leftIndeces = getIndeces(activePage * cardsPerPage - numCardsToTheLeft, numCardsToTheLeft)
  const rightIndeces = getIndeces(activePage * cardsPerPage + cardsPerPage, numCardsToTheRight)
  const getPosition = (index: number) => {
    const cardOffset = pageOffset / cardsPerPage
    const isLeftOfLeft = leftIndeces.length > 0 && index < leftIndeces[0]
    const rightBase = pageOffset
    let position = 0
    const maxLeftOffset =
      totalResults > cardsPerPage && activeIndeces[activeIndeces.length - 1] === totalResults - 1
        ? (cardsPerPage - activeIndeces.length) * cardOffset
        : 0

    if (isLeftOfLeft) {
      position = (leftIndeces.length + 1) * -cardOffset + maxLeftOffset
    }
    const isRightOfRight = rightIndeces.length > 0 && index > rightIndeces[rightIndeces.length - 1]
    if (isRightOfRight) {
      position = rightBase + cardOffset * rightIndeces.length
    }
    const leftIndex = leftIndeces.indexOf(index)
    if (leftIndex > -1) {
      position = (leftIndeces.length - leftIndex) * -cardOffset + maxLeftOffset
    }
    const activeIndex = activeIndeces.indexOf(index)
    if (activeIndex > -1) {
      position = activeIndex * cardOffset + maxLeftOffset
    }
    const rightIndex = rightIndeces.indexOf(index)
    if (rightIndex > -1) {
      position = rightBase + cardOffset * rightIndex
    }
    return position + tempOffset / 2
  }

  return (
    <Results
      onTouchStart={handleTouchStart}
      onTouchMove={!oneCard && captureMovement ? handleTouchMove : undefined}
      onTouchEnd={handleMoveEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={!oneCard && captureMovement ? handleMove : undefined}
      onMouseUp={handleMoveEnd}
      onWheel={handleWheel}
    >
      {title && (
        <Typography variant="h4" color="textPrimary" textAlign="center">
          {title}
        </Typography>
      )}

      <CardsContainer>
        <Cards ref={cardsRef}>
          {results?.map((data, index) => {
            const position = getPosition(index)
            const destinationPage = Math.floor(index / cardsPerPage)
            return (
              <Card
                key={index}
                onFocus={() => {
                  if (activePage !== destinationPage) {
                    setActivePage(destinationPage)
                  }
                }}
                offsetLeft={position}
                onePage={numPages === 1}
                oneResult={numResults === 1}
                perPage={cardsPerPage}
                canAnimate={cardsCanAnimate}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'ArrowLeft') {
                    if (activePage !== 0) {
                      setActivePage(activePage - 1)
                    }
                  } else if (e.key === 'ArrowRight') {
                    if (activePage !== numPages - 1) {
                      setActivePage(activePage + 1)
                    }
                  }
                }}
              >
                <CardContainerOuter>
                  <CardContainerInner>
                    {children && children(data)}
                    {(loading || nextPage) && index === results.length - 1 && (
                      <ProgressBox>
                        <CircularProgress />
                      </ProgressBox>
                    )}
                  </CardContainerInner>
                </CardContainerOuter>
              </Card>
            )
          })}
        </Cards>
      </CardsContainer>
      {numPages > 1 && (
        <DotsContainer>
          <PositionedDots>
            <PaginationDots dots={numPages} activeIndex={activePage} />
          </PositionedDots>
        </DotsContainer>
      )}
    </Results>
  )
}

const Results = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  touch-action: none;
  max-width: ${MAX_RESULTS_WIDTH}px;
  width: 100%;
  margin: auto;
`

const CardsContainer = styled(VBox)`
  position: relative;
  width: 100%;
  left: 0;
  flex: 1;
`
const DotsContainer = styled(Box)`
  position: relative;
  width: 100%:
`
const PositionedDots = styled(Box)`
  position: absolute;
  left: 50%;
  transform: translate(-50%, calc(50% - 20px));
`

const Cards = styled(VBox)`
  position: relative;
  width: auto;
  min-width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  align-items: stretch;
  transition: transform 0.2s cubic-bezier(0, 0, 0, 1);
`

const getCardOpacity = (offsetLeft: number) => {
  const cardIsCentered = offsetLeft >= 0 && offsetLeft < 100
  return cardIsCentered ? 1 : 0.4
}

const Card = styled(
  Box,
  excludeScProps(['offsetLeft', 'oneResult', 'onePage', 'canAnimate', 'perPage'])
)<{
  offsetLeft: number
  oneResult?: boolean
  onePage?: boolean
  canAnimate?: boolean
  perPage?: number
}>`
  position: ${({ oneResult }) => (oneResult ? 'relative' : 'absolute')};
  ${({ oneResult }) => (oneResult ? 'margin: auto;' : '')}
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  outline: none;
  ${({ canAnimate, offsetLeft }) =>
    canAnimate &&
    `transition: left 0.5s cubic-bezier(0.49, 0.63, 0, 0.99), opacity 0.25s linear ${offsetLeft < 0 ? '0.25s' : '0s'};`}
  ${({ theme, oneResult, perPage, offsetLeft }) => {
    return `
      left: ${offsetLeft}%;
      flex: ${oneResult ? '1' : '0 0 100%'};
      padding: ${theme.spacing(sitePaddingHorz * 0.25)};
      max-width: 100%;
      opacity: ${getCardOpacity(offsetLeft)};

      ${theme.breakpoints.down('md')} {
        padding: ${theme.spacing(1)} ${theme.spacing(sitePaddingHorz * 0.125)} ${theme.spacing(2)};
      }
      ${
        perPage == 2 &&
        `
        flex: ${oneResult ? '1' : '0 0 50%'};
        max-width: 50%;
      `
      }
      ${
        perPage == 3 &&
        `
        flex: ${oneResult ? '1' : '0 0 33.33%'};
        max-width: 33.33%;
      `
      }
    `
  }}
`

const CardContainerOuter = styled(Box)`
  position: relative;
  flex: 1;
`
const CardContainerInner = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
`

const ProgressBox = styled(VBox)`
  ${({ theme }) => `
    position: absolute;
    left: calc(100% +  ${theme.spacing(2)});
    top: 0;
    height: 100%;
    width: 100%;
    opacity: 50%;
    align-items: center;
    justify-content: center;
    border-radius: ${theme.shape.borderRadius}px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_80 : colors.WHITE};
  `}
`

export default ResultsList
